import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, Search } from 'lucide-react';
import { cn } from '@eombor/ui';
import { t } from '../i18n/index.js';
import { useFieldAppearance } from './field-appearance.js';
import {
  computePlacement,
  useAnchoredPlacement,
  type AnchoredPlacement,
} from './anchored-placement.js';

export interface SelectOption {
  value: string;
  label: string;
  /** Ikkilamchi belgi (kod) — nomdan alohida, xira qilib ko'rsatiladi. */
  hint?: string | null;
}

/** `hint` ni alohida chiza olmaydigan joylar uchun — nom va kod bitta satrda. */
const joinedLabel = (option: SelectOption): string =>
  option.hint === null || option.hint === undefined || option.hint === ''
    ? option.label
    : `${option.label} · ${option.hint}`;

/**
 * `<select>` ning `change` hodisasi bilan BIR SHAKLDA.
 *
 * Element endi xom `<select>` emas, lekin yigirmata chaqiruv joyi
 * `event.target.value` deb yozilgan va ularga haqiqatda kerak bo'lgani shu
 * bitta qiymat. Interfeys saqlangani uchun ko'chirishda birorta sahifaning
 * mantig'i o'zgarmadi.
 */
export interface SelectChangeEvent {
  target: { value: string };
}

export interface SelectProps {
  label?: ReactNode;
  /** Ko'rinmas yorliq — jadval boshi ustunni allaqachon nomlagan joylarda. */
  hiddenLabel?: boolean;
  error?: string;
  hint?: string;
  options: readonly SelectOption[];
  /** Birinchi (bo'sh) variant matni — berilmasa majburiy tanlov bo'ladi. */
  placeholder?: string;
  value?: string;
  onChange?: (event: SelectChangeEvent) => void;
  disabled?: boolean;
  required?: boolean;
  id?: string;
  className?: string;
  /**
   * `field` — forma maydoni: 44px, to'liq kenglik, yorliq 14px.
   * `filter` — sahifa sarlavhasidagi filtr: 40px (tugma bilan bir chiziqda),
   * 200–280px, yorliq 12px, tanlangani chegara rangi bilan bildiriladi.
   */
  variant?: 'field' | 'filter';
}

/** Shundan ko'p variant bo'lsa ro'yxat tepasida qidiruv chiqadi. */
const SEARCH_THRESHOLD = 8;
const MAX_LIST_HEIGHT = 280;
/* Panelning ro'yxatdan TASHQARIDAGI balandligi: `p-1` (8) + chegara (2),
   qidiruv bo'lsa yana maydon (36) va uning ostidagi `mb-1` (4). Joyni
   o'lchashda shular ham hisobga olinadi — aks holda qidiruvli panel
   hisoblanganidan ~50px uzun chiqib, ekran pastidan kesilardi. */
const PANEL_CHROME = 10;
const SEARCH_CHROME = 40;
/** Oynadagi panel `p-1.5` — `p-1` dan har tomonda 2px ortiq. */
const REFINED_EXTRA_PADDING = 4;

/**
 * RO'YXATDAN TANLASH.
 *
 * Nega brauzerning `<select>` i emas: uning ochilgan ro'yxatini CSS bilan
 * bezab bo'lmaydi — uni operatsion tizim chizadi. Natijada bitta ilovada
 * Windows ning kulrang ro'yxati, macOS ning oq paneli va Android ning
 * to'liq ekranli oynasi paydo bo'lardi. Bank interfeysida ro'yxat ham
 * qolgan hamma narsa kabi bir xil ko'rinishi kerak.
 *
 * O'rniga ARIA ning `listbox` naqshi: tugma + `role="listbox"`. Klaviatura
 * va ekran o'qigich uchun bu `<select>` bilan bir xil qoladi — o'q yuradi,
 * `Enter` tanlaydi, `Esc` yopadi, fokus tugmaga qaytadi.
 *
 * Ro'yxat PORTALDA va `position: fixed`: `Combobox` dagi kabi, u forma
 * balandligini o'zgartirmasin va oyna tanasining `overflow` chegarasida
 * kesilib qolmasin.
 */
export const Select = ({
  label,
  hiddenLabel = false,
  error,
  hint,
  options,
  placeholder,
  value = '',
  onChange,
  disabled = false,
  required = false,
  id,
  className,
  variant = 'field',
}: SelectProps) => {
  const autoId = useId();
  const triggerId = id ?? autoId;
  const refined = useFieldAppearance() === 'refined' && variant === 'field';
  const listId = `${triggerId}-list`;
  const errorId = `${triggerId}-error`;
  const hintId = `${triggerId}-hint`;

  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [placement, setPlacement] = useState<AnchoredPlacement | null>(null);

  /* Bo'sh variant ham ro'yxatning oddiy qatori: "barcha holatlar" ni
     tanlash ham TANLOV va u boshqalari bilan bir xil yurishi kerak. */
  const items = useMemo<SelectOption[]>(
    () =>
      placeholder === undefined
        ? [...options]
        : [{ value: '', label: placeholder }, ...options],
    [options, placeholder],
  );

  const searchable = items.length >= SEARCH_THRESHOLD;

  const visible = useMemo(() => {
    if (!searchable || query.trim() === '') return items;
    const needle = query.trim().toLowerCase();
    return items.filter((item) => joinedLabel(item).toLowerCase().includes(needle));
  }, [items, query, searchable]);

  const selected = items.find((item) => item.value === value) ?? null;
  const empty = value === '';

  const placementOptions = {
    maxListHeight: MAX_LIST_HEIGHT,
    chrome: PANEL_CHROME + (refined ? REFINED_EXTRA_PADDING : 0) + (searchable ? SEARCH_CHROME : 0),
  };

  const measure = (): AnchoredPlacement | null =>
    triggerRef.current === null ? null : computePlacement(triggerRef.current, placementOptions);

  useAnchoredPlacement({
    open,
    anchorRef: triggerRef,
    panelRef: listRef,
    options: placementOptions,
    onPlace: setPlacement,
    onDetach: () => {
      setOpen(false);
      setQuery('');
    },
  });

  /* Qidiruv maydoni bo'lsa fokus O'SHA YERGA ketadi: uzun ro'yxatni ochgan
     odam darrov terishni boshlaydi. Bo'lmasa fokus tugmada qoladi va
     qatorlar bo'ylab yurish `aria-activedescendant` bilan e'lon qilinadi. */
  useEffect(() => {
    if (open && searchable) searchRef.current?.focus();
  }, [open, searchable]);

  /* Tanlangan qator ko'rinib tursin: 200 ta ombordan 187-si tanlangan
     bo'lsa, ro'yxatning boshidan ochilishi "hech nima tanlanmagan" bo'lib
     ko'rinardi. */
  useEffect(() => {
    if (!open) return;
    listRef.current
      ?.querySelector(`[data-index="${active}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [open, active]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent): void => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (triggerRef.current?.contains(target) === true) return;
      if (listRef.current?.contains(target) === true) return;
      // Tashqariga bosilganda fokus QAYTARILMAYDI: odam boshqa maydonga
      // bosgan bo'ladi va tugma uning fokusini o'g'irlab olardi.
      setOpen(false);
      setQuery('');
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const close = (refocus: boolean): void => {
    setOpen(false);
    setQuery('');
    if (refocus) triggerRef.current?.focus();
  };

  const openList = (): void => {
    if (disabled) return;
    const index = items.findIndex((item) => item.value === value);
    setActive(index < 0 ? 0 : index);
    setQuery('');
    /* Joy SHU YERDA o'lchanadi, effektda emas: aks holda `open` bo'lgan
       birinchi kadrda `placement` hali `null` bo'lib, ro'yxat umuman
       chizilmasdi — va o'sha kadrda ishlaydigan fokus hamda "tanlanganni
       ko'rinishga surish" effektlari bo'shliqqa tegardi. */
    setPlacement(measure());
    setOpen(true);
  };

  const pick = (option: SelectOption): void => {
    onChange?.({ target: { value: option.value } });
    close(true);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    if (!open) {
      if (
        event.key === 'Enter' ||
        event.key === ' ' ||
        event.key === 'ArrowDown' ||
        event.key === 'ArrowUp'
      ) {
        event.preventDefault();
        openList();
      }
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActive((current) => {
        if (visible.length === 0) return 0;
        return (current + step + visible.length) % visible.length;
      });
      return;
    }

    if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      setActive(event.key === 'Home' ? 0 : Math.max(0, visible.length - 1));
      return;
    }

    if (event.key === 'Enter') {
      const option = visible[active];
      if (option !== undefined) {
        event.preventDefault();
        pick(option);
      }
      return;
    }

    if (event.key === 'Escape') {
      // Oyna ichida ishlaydi: `Esc` ro'yxatni yopadi, modalni EMAS.
      event.stopPropagation();
      event.preventDefault();
      close(true);
      return;
    }

    if (event.key === 'Tab') close(false);
  };

  const describedBy = cn(error ? errorId : undefined, !error && hint ? hintId : undefined);
  const filter = variant === 'filter';
  const activeId = visible[active] === undefined ? undefined : `${listId}-${active}`;

  return (
    <div
      className={cn(
        'flex min-w-0 flex-col gap-1.5',
        /* 200–280px: qisqasi yorliqni sig'dirmaydi, uzunidan keyin esa
           tanlagich sarlavhaning o'zidan kengroq bo'lib ketadi. Telefonda
           bu chegara olib tashlanadi — o'sha yerda u to'liq kenglikda. */
        filter ? 'max-md:w-full max-md:max-w-none min-w-[12.5rem] max-w-[17.5rem]' : undefined,
        className,
      )}
    >
      {label ? (
        <label
          htmlFor={triggerId}
          className={cn(
            'font-medium',
            filter ? 'text-text-muted text-xs' : 'text-text text-sm',
            hiddenLabel ? 'sr-only' : undefined,
          )}
        >
          {label}
          {required ? (
            <span aria-hidden className="text-danger ms-0.5">
              *
            </span>
          ) : null}
        </label>
      ) : null}

      <button
        ref={triggerRef}
        id={triggerId}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open && !searchable ? activeId : undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy.length > 0 ? describedBy : undefined}
        aria-required={required || undefined}
        onClick={() => (open ? close(true) : openList())}
        onKeyDown={onKeyDown}
        className={refined ? cn(
          'field-refined group relative flex w-full min-w-0 items-center border',
          'h-[var(--size-control)] py-0 pr-9 pl-3 text-left text-base',
        ) : cn(
          'relative flex w-full min-w-0 items-center rounded-[var(--radius-sm)]',
          'bg-surface border py-0 pr-9 pl-3 text-left',
          'transition-[border-color,box-shadow] duration-[var(--motion-fast)]',
          'ease-[var(--ease-standard)]',
          'disabled:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-60',
          'focus-visible:border-accent',
          filter
            ? 'h-[var(--size-control-md)] text-sm font-medium'
            : 'h-[var(--size-control)] text-base',
          error
            ? 'border-danger'
            : /* Filtr YOQILGANini chegara aytadi: sarlavhada bir necha
                 tanlagich yonma-yon turadi va qaysi biri ro'yxatni
                 qisqartirayotgani bir qarashda ko'rinishi kerak. */
              filter && !empty
              ? 'border-accent'
              : filter
                ? // Sarlavhadagi filtr uchun hover BREND navysiga o'tadi
                  // (spetsifikatsiya): u yerda tanlagich yolg'iz turadi.
                  // Forma ichida esa `Input` bilan bir xil qoladi — yonma-yon
                  // turgan ikki maydon hover da har xil rang bermasin.
                  'border-border-control hover:border-primary-300'
                : 'border-border-control hover:border-border-strong',
        )}
      >
        <span
          className={cn(
            'min-w-0 flex-1 truncate',
            refined
              ? empty
                ? 'text-text-faint/75 group-disabled:text-text-faint/55'
                : 'text-text group-disabled:text-text-faint/70'
              : empty
                ? 'text-text-muted'
                : 'text-text',
          )}
        >
          {selected === null ? (placeholder ?? '') : refined ? selected.label : joinedLabel(selected)}
        </span>
        {refined && !empty && selected?.hint ? (
          <span className="tabular text-text-faint group-disabled:text-text-faint/60 ms-2 shrink-0 text-xs">
            {selected.hint}
          </span>
        ) : null}
        <ChevronDown
          size={16}
          aria-hidden
          className={cn(
            'text-text-muted pointer-events-none absolute right-3',
            refined ? 'group-disabled:opacity-50' : undefined,
            'transition-transform duration-[180ms] ease-[var(--ease-standard)]',
            open ? 'rotate-180' : undefined,
          )}
        />
      </button>

      {error ? (
        <p id={errorId} role="alert" className="motion-field-error text-danger text-xs">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="text-text-faint text-xs">
          {hint}
        </p>
      ) : null}

      {open && placement !== null
        ? createPortal(
            <div
              ref={listRef}
              style={{
                position: 'fixed',
                left: placement.left,
                minWidth: placement.width,
                maxWidth: placement.maxWidth,
                top: placement.top,
                bottom: placement.bottom,
              }}
              className={cn(
                'motion-listbox-in z-[var(--z-popover)]',
                'border-border bg-surface rounded-[var(--radius-md)] border',
                refined ? 'p-1.5 shadow-[var(--shadow-3)]' : 'p-1 shadow-[var(--shadow-2)]',
              )}
            >
              {searchable ? (
                <div className="relative mb-1 flex items-center">
                  <Search
                    size={14}
                    aria-hidden
                    className="text-text-faint pointer-events-none absolute left-2.5"
                  />
                  <input
                    ref={searchRef}
                    role="combobox"
                    aria-expanded
                    aria-controls={listId}
                    aria-activedescendant={activeId}
                    aria-autocomplete="list"
                    aria-label={t('form.multiSearch')}
                    autoComplete="off"
                    value={query}
                    onChange={(event) => {
                      setQuery(event.target.value);
                      setActive(0);
                    }}
                    onKeyDown={onKeyDown}
                    className={cn(
                      'border-border-control h-9 w-full rounded-[var(--radius-sm)] border',
                      'bg-surface text-text placeholder:text-text-faint pr-2 pl-8 text-sm',
                    )}
                  />
                </div>
              ) : null}

              <ul
                id={listId}
                role="listbox"
                aria-labelledby={triggerId}
                style={{ maxHeight: placement.listMaxHeight }}
                className="overflow-y-auto overscroll-contain"
              >
                {visible.length === 0 ? (
                  <li className="text-text-faint px-3 py-2.5 text-sm">{t('form.multiEmpty')}</li>
                ) : (
                  visible.map((option, index) => {
                    const picked = option.value === value;
                    /* Oynada bo'sh variant ("Korxonani tanlang") TANLOV kabi
                       bo'yalmaydi: u ko'k fon va ✓ bilan chiqsa, hech narsa
                       tanlanmagan holat "tanlangan"dek ko'rinardi. */
                    const blank = refined && placeholder !== undefined && option.value === '';
                    if (refined) {
                      return (
                        <li
                          key={option.value}
                          className={blank ? 'border-border mb-1 border-b pb-1' : undefined}
                        >
                          <button
                            type="button"
                            id={`${listId}-${index}`}
                            data-index={index}
                            role="option"
                            aria-selected={picked}
                            onMouseDown={(event) => {
                              event.preventDefault();
                              pick(option);
                            }}
                            onMouseEnter={() => setActive(index)}
                            className={cn(
                              'flex min-h-11 w-full items-center gap-3 rounded-[var(--radius-sm)] px-3 py-2',
                              'text-left text-sm',
                              'transition-colors duration-[var(--motion-fast)]',
                              picked && !blank ? 'bg-accent-soft' : undefined,
                              index === active && !(picked && !blank) ? 'bg-surface-2' : undefined,
                            )}
                          >
                            <span
                              className={cn(
                                'min-w-0 flex-1 line-clamp-2',
                                blank
                                  ? 'text-text-faint'
                                  : picked
                                    ? 'text-accent font-medium'
                                    : 'text-text',
                              )}
                            >
                              {option.label}
                            </span>
                            {option.hint ? (
                              <span
                                className={cn(
                                  'tabular shrink-0 rounded-[0.375rem] px-1.5 py-0.5 text-[0.6875rem]',
                                  picked
                                    ? 'bg-surface/70 text-accent'
                                    : 'bg-surface-2 text-text-muted',
                                )}
                              >
                                {option.hint}
                              </span>
                            ) : null}
                            {picked && !blank ? (
                              <Check size={16} aria-hidden className="text-accent shrink-0" />
                            ) : null}
                          </button>
                        </li>
                      );
                    }
                    return (
                      <li key={option.value}>
                        <button
                          type="button"
                          id={`${listId}-${index}`}
                          data-index={index}
                          role="option"
                          aria-selected={picked}
                          // `blur` dan oldin ishlashi uchun `mousedown`.
                          onMouseDown={(event) => {
                            event.preventDefault();
                            pick(option);
                          }}
                          onMouseEnter={() => setActive(index)}
                          className={cn(
                            'flex h-10 w-full items-center gap-2 rounded-[var(--radius-sm)] px-3',
                            'text-text text-left text-sm',
                            picked ? 'bg-accent-soft' : undefined,
                            index === active && !picked ? 'bg-surface-2' : undefined,
                          )}
                        >
                          <span className="min-w-0 flex-1 truncate">{joinedLabel(option)}</span>
                          {picked ? (
                            <Check size={16} aria-hidden className="text-accent shrink-0" />
                          ) : null}
                        </button>
                      </li>
                    );
                  })
                )}
              </ul>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
};
